import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';

export interface InputProps {
  type?: 'text' | 'email' | 'number' | 'password' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local';
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  autoFocus?: boolean;
  autoComplete?: string;
  readOnly?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onChange?: (value: string | number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEnter?: () => void;
}

const Input: React.FC<InputProps> = ({
  type = 'text',
  value,
  defaultValue,
  placeholder = '',
  label,
  disabled = false,
  loading = false,
  clearable = false,
  size = 'md',
  className = '',
  error,
  helperText,
  required = false,
  maxLength,
  min,
  max,
  step,
  autoFocus = false,
  autoComplete,
  readOnly = false,
  leftIcon,
  rightIcon,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  onEnter,
}) => {
  const [internalValue, setInternalValue] = useState<string | number>(value ?? defaultValue ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update internal value when prop changes
  useEffect(() => {
    if (value !== undefined && (type !== 'time' || !isFocused)) {
      setInternalValue(value);
    }
  }, [value, type, isFocused]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue =
      type === 'number'
        ? e.target.value === ''
          ? ''
          : Number(e.target.value)
        : e.target.value;
    // Mirror time inputs locally so React does not force-write the stale value back
    // during native HH:mm segment editing (which breaks digit placement). Other
    // controlled types keep the sanitized behavior: do not mirror raw input locally or
    // invalid characters can flash / stick until the parent re-renders.
    if (type === 'time' || value === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = '';
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
    inputRef.current?.focus();
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  // Handle blur
  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  // Handle key events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnter) {
      e.preventDefault();
      onEnter();
    }
    onKeyDown?.(e);
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Size classes
  const sizeClasses = {
    sm: 'h-8 text-sm',
    md: 'h-10 text-sm',
    lg: 'h-12 text-base',
  };

  // Check if input has value
  const hasValue = internalValue !== '' && internalValue !== null && internalValue !== undefined;

  // Check if should show clear button
  const showClear = clearable && hasValue && !disabled && !loading && !readOnly;

  // Check if should show password toggle
  const showPasswordToggle = type === 'password' && !disabled && !loading;

  // Check if has right side content
  const hasRightContent = showClear || showPasswordToggle || rightIcon || loading;

  // Padding classes based on icons
  const getPaddingClasses = () => {
    if (leftIcon && hasRightContent) {
      return size === 'sm' ? 'pl-8 pr-8' : size === 'md' ? 'pl-10 pr-10' : 'pl-12 pr-12';
    } else if (leftIcon) {
      return size === 'sm' ? 'pl-8 pr-2' : size === 'md' ? 'pl-10 pr-3' : 'pl-12 pr-4';
    } else if (hasRightContent) {
      return size === 'sm' ? 'pl-2 pr-8' : size === 'md' ? 'pl-3 pr-10' : 'pl-4 pr-12';
    } else {
      return size === 'sm' ? 'px-2' : size === 'md' ? 'px-3' : 'px-4';
    }
  };

  const iconSizes = {
    sm: 14,
    md: 16,
    lg: 18,
  };

  // Determine input type (for password toggle)
  const inputType = type === 'password' && showPassword ? 'text' : type;

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
          {label}
          {required && <span className="text-[var(--color-error)] ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-tertiary)]">
            {leftIcon}
          </div>
        )}

        {/* Input */}
        <input
          ref={inputRef}
          type={inputType}
          value={value !== undefined ? value : internalValue}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={onKeyUp}
          onClick={(e) => {
            if (type === 'time' || type === 'date') {
              try {
                (e.target as any).showPicker?.();
              } catch (err) {}
            }
          }}
          placeholder={placeholder}
          disabled={disabled || loading}
          readOnly={readOnly}
          required={required}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className={`
            w-full rounded-[9px] border transition-all duration-200 placeholder:text-[var(--color-text-placeholder)]
            ${sizeClasses[size]} ${getPaddingClasses()}
            ${isFocused
              ? 'border-[var(--color-border-focus)] shadow-[0_0_0_3px_rgba(109,40,217,0.14)]'
              : 'border-[var(--color-border-primary)] hover:border-[var(--color-border-input-hover)]'
            }
            ${disabled || loading
              ? 'bg-[var(--color-bg-disabled)] text-[var(--color-text-disabled)] cursor-not-allowed'
              : 'bg-[var(--color-bg-muted)] text-[var(--color-text-primary)]'
            }
            ${error
              ? 'border-[var(--color-error)] focus:shadow-[0_0_0_3px_rgba(239,68,68,0.2)] focus:border-[var(--color-error)]'
              : 'focus:shadow-[0_0_0_3px_rgba(109,40,217,0.14)] focus:border-[var(--color-border-focus)]'
            }
          `}
        />

        {/* Right Icons Container */}
        {(showClear || showPasswordToggle || rightIcon || loading) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {/* Loading Spinner */}
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-primary-600)]" />
            )}

            {/* Clear Button */}
            {showClear && !loading && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-[var(--color-bg-muted)] rounded transition-colors"
                tabIndex={-1}
              >
                <X size={iconSizes[size]} className="text-[var(--color-text-tertiary)]" />
              </button>
            )}

            {/* Password Toggle */}
            {showPasswordToggle && !loading && (
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="p-1 hover:bg-[var(--color-bg-muted)] rounded transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff size={iconSizes[size]} className="text-[var(--color-text-tertiary)]" />
                ) : (
                  <Eye size={iconSizes[size]} className="text-[var(--color-text-tertiary)]" />
                )}
              </button>
            )}

            {/* Right Icon */}
            {rightIcon && !loading && !showClear && !showPasswordToggle && (
              <div className="text-[var(--color-text-tertiary)]">
                {rightIcon}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      <div className="mt-1 flex justify-between">
        {error ? (
          <div className="text-xs text-[var(--color-error)]">{error}</div>
        ) : helperText ? (
          <div className="text-xs text-[var(--color-text-secondary)]">{helperText}</div>
        ) : <div />}
      </div>
    </div>
  );
};

export default Input;

